<!-- eslint-disable no-console -->
<script setup lang="ts">
import AccountPreferencesPane from '@/components/Settings/AccountPreferences.vue'
import UserPreferencesPane from '@/components/Settings/UserPreferences.vue'
import AppState, { isRentalsEnabled } from '@/states/app'
import {
  mountMeetingStatusSettings,
  unmountMeetingStatusSettings,
} from '@/utils/remoteAppIntegration'
import { UIHeader, UITabPane, UITabs } from '@gohighlevel/ghl-ui'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  watchEffect,
} from 'vue'
import { useRoute } from 'vue-router'

const APPOINTMENT_STATUS_TAB = 'appointment-status'
const STATUS_SETTINGS_MOUNT_ID = 'MeetingStatusSettings'

const route = useRoute()
const tabType = ref(route.query?.tab || 'my-preference')
const meetingStatusSettingsApp = ref<any>('')

const isUserPartOfLocation = computed(() => {
  return !!AppState?.isUserPartOfLocation
})
const showStandaloneChrome = computed(
  () =>
    !AppState.isCalendarTypeSplitEnabled &&
    !AppState.isServicesEnabled &&
    !isRentalsEnabled.value
)
/** Appointment statuses ride the `calendars.customStatuses` rollout flag the parent app resolves. */
const showAppointmentStatusTab = computed(() => AppState.isCustomStatusesEnabled)

watchEffect(() => {
  if (!isUserPartOfLocation.value) {
    tabType.value = 'account-preference'
  }
})

const unmountStatusSettings = () => {
  unmountMeetingStatusSettings()
  meetingStatusSettingsApp.value = null
}

const mountStatusSettings = () => {
  mountMeetingStatusSettings(STATUS_SETTINGS_MOUNT_ID, {
    locale: AppState.locale,
    locationId: AppState.locationId,
    onError: (error: unknown) => {
      console.error('Error in meeting status settings:', error)
    },
  })
}

/**
 * Mounts or tears down the status-settings remote to match the selected tab.
 *
 * Called from both the watcher and `onMounted` — the watcher is not `immediate`
 * (an immediate run happens during `setup()`, before the pane exists), so a
 * remount while the status tab is already selected would otherwise leave an
 * empty pane behind the teardown in `onBeforeUnmount`.
 */
const syncStatusSettingsRemote = async () => {
  const wantsStatusTab = tabType.value === APPOINTMENT_STATUS_TAB

  // `locationId` lands with the same handshake as the flag, so once it is set
  // the flag is known: a deep link to a tab this location cannot see falls back
  // no earlier than that.
  if (
    wantsStatusTab &&
    AppState.locationId &&
    !showAppointmentStatusTab.value
  ) {
    unmountStatusSettings()
    tabType.value = 'my-preference'
    return
  }

  if (
    wantsStatusTab &&
    showAppointmentStatusTab.value &&
    AppState.locationId &&
    AppState.tokenId
  ) {
    // The pane is only rendered while it is the active tab, so wait for it.
    await nextTick()
    mountStatusSettings()
    return
  }

  unmountStatusSettings()
}

watch(
  () => [
    AppState.locationId,
    AppState.tokenId,
    showAppointmentStatusTab.value,
    tabType.value,
  ],
  syncStatusSettingsRemote
)

onMounted(() => {
  AppState.globalTeams = []
  AppState.calendarsOnboarding = []
  void syncStatusSettingsRemote()
})

onBeforeUnmount(() => {
  unmountStatusSettings()
})
</script>

<template>
  <div :class="{ 'p-8': showStandaloneChrome }">
    <UIHeader
      v-if="showStandaloneChrome"
      id="calendar-preferences-settings-headers"
      :title="$t('settings.preferences.preferences')"
    />

    <UITabs v-model:value="tabType" type="line">
      <UITabPane
        v-if="isUserPartOfLocation"
        name="my-preference"
        :tab="$t('settings.preferences.my_preferences')"
        class="mt-6"
      >
        <UserPreferencesPane />
      </UITabPane>

      <UITabPane
        name="account-preference"
        :tab="$t('settings.preferences.account_preferences')"
        class="mt-6"
      >
        <AccountPreferencesPane />
      </UITabPane>

      <UITabPane
        v-if="showAppointmentStatusTab"
        :name="APPOINTMENT_STATUS_TAB"
        :tab="$t('settings.preferences.appointment_status')"
        class="mt-6"
      >
        <div :id="STATUS_SETTINGS_MOUNT_ID" ref="meetingStatusSettingsApp" />
      </UITabPane>
    </UITabs>
  </div>
</template>
